<template>
    <DefaultLayout :meta="meta">

        <div class="w-full bg-gray-50" ref="progressbar">

            <div class="w-full max-w-screen-xl mx-auto px-6 py-6">

                <div class="relative">

                    <div class="absolute left-0 top-0 mt-1">
                        <button type="button" class="text-gray-500 flex items-center" @click="back()" v-if="step != 'upload'">
                            <ChevronLeftIcon class="h-4 w-4 mr-2 inline-block" />
                            <span class="hidden lg:inline-block">{{ __('home::orders.back') }}</span>
                        </button>
                    </div>

                    <h2 class="order-2 font-bold text-center">
                        {{ stepTitle }}
                    </h2>

                </div>

            </div>

        </div>

        <Upload
            v-if="step === 'upload'"
            :order="order"
            :nextDelivery="nextDelivery"
            v-model:photos="order.photos"
            @continue="next('upload')"
        />

        <Type
            v-if="step === 'type'"
            :order="order"
            v-model:type="order.meta.type"
            @update:type="next('type')"
        />

        <Shape
            v-if="step === 'shape'"
            :order="order"
            v-model:shape="order.meta.shape"
            @update:shape="next('shape')"
        />

        <Template
            v-if="step === 'template'"
            :order="order"
            :products="products"
            v-model:template="order.meta.template"
            @update:template="next('template')"
        />

        <Photos
            v-if="step === 'photos'"
            :order="order"
            :products="products"
            :subtotalPrice="subtotalPrice"
            :deliveryPrice="deliveryPrice"
            :delayedPaymentDays="delayedPaymentDays"
            :nextDelivery="nextDelivery"
            v-model:photos="order.photos"
            @next="next('photos')"
        />

        <PhotosSpread
            v-if="step === 'photos-spread'"
            :order="order"
            :products="products"
            :subtotalPrice="subtotalPrice"
            :deliveryPrice="deliveryPrice"
            v-model:photos="order.photos"
            @next="next('photos-spread')"
        />

        <Accessories
            v-if="step === 'accessories'"
            :order="order"
            :products="products"
            v-model:accessory="order.meta.accessory"
            @update:accessory="next('accessories')"
        />

        <div class="product-creator flex justify-center items-center w-full max-w-screen-xl h-full mx-auto px-6 py-12" v-if="step === 'prepare'">
            <div>

                <svg class="mt-12 h-12 w-12 animate-spin mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            </div>

        </div>

    </DefaultLayout>
</template>

<script>
    import axios from 'axios'
    import collect from 'collect.js'

    import Upload from '@H/Pages/Orders/Steps/Upload'
    import Type from '@H/Pages/Orders/Steps/Type'
    import Shape from '@H/Pages/Orders/Steps/Shape'
    import Template from '@H/Pages/Orders/Steps/Template'
    import Photos from '@H/Pages/Orders/Steps/Photos'
    import PhotosSpread from '@H/Pages/Orders/Steps/PhotosSpread'
    import Accessories from '@H/Pages/Orders/Steps/Accessories'

    import {
        ChevronLeftIcon,
    } from '@heroicons/vue/outline'

    export default {
        components: {
            ChevronLeftIcon,
            Upload,
            Type,
            Shape,
            Template,
            Photos,
            PhotosSpread,
            Accessories,
        },
        props: {
            meta: Object,
            products: Object,
            initialOrder: Object,
            cheapestDeliveryPrice: Number,
            delayedPaymentDays: Number|String,
        },
        data() {
            return {
                order: this.initialOrder,
                step: this.fetchCurrentStep(),
                steps: [
                    { key: 'upload', title: this.__('home::orders.step_title_upload') },
                    //{ key: 'type', title: this.__('home::orders.step_title_type') }, // modify back-btn as well
                    { key: 'shape', title: this.__('home::orders.step_title_shape'), types: ['multiple'] },
                    { key: 'template', title: this.__('home::orders.step_title_template'), types: ['multiple'] },
                    { key: 'photos', title: this.__('home::orders.step_title_photos'), types: ['single'] },
                    { key: 'photos-spread', title: this.__('home::orders.step_title_photos_spread'), types: ['multiple'] },
                    //{ key: 'accessories', title: this.__('home::orders.step_title_accessories') },
                    { key: 'prepare', title: this.__('home::orders.step_title_prepare') },
                ],
            }
        },
        mounted() {
            this.$gtm.trackEvent({
                event: 'BeginPhotoCreation',
                category: 'PhotoCreation',
            });
        },
        computed: {
            stepTitle() {
                var step = this.steps.find(x => x.key == this.step);
                return step.title;
            },
            subtotalPrice() {
                var amount = collect(this.order.photos)
                    .sum(photo => {
                        return photo.product.price_in_currency;
                    });

                amount = amount + amount * this.order.vat_rate;

                return parseFloat(amount);
            },
            deliveryPrice() {
                if (this.order.photos.length >= 4) {
                    return 0;
                }

                return parseFloat(this.cheapestDeliveryPrice);
            },
            nextDelivery() {
                var date = new Date();
                //var deliveryDays = date.getHours() >= 14 ? 5 : 4;
                var deliveryDays = 2;
                var currentDay = date.getDay();

                date.setDate(date.getDate() + deliveryDays);

                if ((currentDay + deliveryDays) > 5) {
                    date.setDate(date.getDate() + 2);
                }
                else if(currentDay == 0) {
                    //date.setDate(date.getDate() + 1);
                }

                var day = date.getDay();

                var days = {
                    0: this.__('forms::labels.sunday'),
                    1: this.__('forms::labels.monday'),
                    2: this.__('forms::labels.tuesday'),
                    3: this.__('forms::labels.wednesday'),
                    4: this.__('forms::labels.thursday'),
                    5: this.__('forms::labels.friday'),
                    6: this.__('forms::labels.saturday'),
                };

                return days[day].toLowerCase();
            },
        },
        methods: {
            fetchCurrentStep() {
                if (this.initialOrder.photos.length === 0) {
                    return 'upload';
                }
                if (! this.initialOrder.meta?.type) {
                    return 'type';
                }
                if (this.initialOrder.meta.type == 'multiple' && ! this.initialOrder.meta?.shape) {
                    return 'shape';
                }
                if (this.initialOrder.meta?.type == 'multiple' && ! this.initialOrder.meta?.template) {
                    return 'template';
                }
                if (this.initialOrder.meta?.type == 'multiple') {
                    return 'photos-spread';
                }
                else {
                    return 'photos';
                }
            },
            back() {
                var steps = this.steps.filter(x => x.types === undefined || x.types.includes(this.order.meta.type));
                var currentIndex = steps.findIndex(x => x.key == this.step);
                var prevIndex = currentIndex > 0 ? currentIndex - 1 : 0;
                this.step = steps[prevIndex].key;

                this.scrollToTop();
            },
            next(current) {
                var steps = this.steps.filter(x => x.types === undefined || x.types.includes(this.order.meta.type));
                var currentIndex = steps.findIndex(x => x.key == current);
                var nextIndex = currentIndex + 1;
                this.step = steps[nextIndex].key;

                if (this.step === 'prepare') {
                    axios
                        .post(this.$route('home::orders.prepare', { uuid: this.order.uuid }))
                        .then((res) => {
                            this.$gtm.trackEvent(res.data.dataLayer);
                            this.$inertia.visit(this.$route('home::checkout.cart'));
                        });
                }

                this.scrollToTop();
            },
            scrollToTop() {
                const progressBarContainer = this.$refs.progressbar;
                const progressBarContainerOffset = progressBarContainer.getBoundingClientRect().top;

                if (0 > progressBarContainerOffset) {
                    progressBarContainer.scrollIntoView({
                        behavior: 'smooth',
                    });
                }
            },
        },
    }
</script>
